import React, { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import pollServices from "../../../services/pollServices";
import { useSiteContext } from "../../../context/SiteContext";
import logger from '../../../utils/logger';

const CreatePolls = () => {
  const navigate = useNavigate();
  const { user } = useSiteContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  //   const { publish: publishPoll } = usePubSub("POLL", {
  //     onMessageReceived: ({ message, senderId }) => {
  //       // Handle poll messages if needed
  //       logger("log", "Poll received:", message, "from:", senderId);
  //     },
  //   });

  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [pollSettings, setPollSettings] = useState({
    shareResultsLive: false,
    showResultsAtEnd: false,
    allowMultipleAnswers: false,
    anonymousVoting: false,
    showParticipantsWhoVoted: false,
    showTotalVotesCountOnly: false,
    autoCloseAfter: false,
    autoCloseSeconds: 60,
  });

  const handleAddPollOption = () => {
    setPollOptions([...pollOptions, ""]);
  };

  const handleUpdatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const handleRemovePollOption = (index) => {
    if (pollOptions.length > 2) {
      const newOptions = pollOptions.filter((_, i) => i !== index);
      setPollOptions(newOptions);
    }
  };

  const handleTogglePollSetting = (setting) => {
    setPollSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };

  const handleUpdateAutoCloseSeconds = (seconds) => {
    setPollSettings((prev) => ({
      ...prev,
      autoCloseSeconds: parseInt(seconds) || 60,
    }));
  };

  const handleSubmitPoll = async () => {
    // Validate poll
    if (!pollQuestion.trim()) {
      toast.error("Please enter a question for the poll.");
      return;
    }

    const validOptions = pollOptions.filter((opt) => opt.trim() !== "");
    if (validOptions.length < 2) {
      toast.error("Please add at least 2 options for the poll.");
      return;
    }

    if (validOptions.length > 10) {
      toast.error("Maximum 10 options allowed.");
      return;
    }

    // Get user ID
    const createdBy = user?.id || user?._id;
    if (!createdBy) {
      toast.error("User information not found. Please log in again.");
      return;
    }

    // Create poll object for API
    const pollData = {
      question: pollQuestion.trim(),
      options: validOptions.map((opt) => opt.trim()),
      settings: pollSettings,
      status: "active",
      createdBy,
    };

    setIsSubmitting(true);

    try {
      const response = await pollServices.createPoll(pollData);

      if (response.success) {
        toast.success("Poll created successfully!");

        // Reset form
        setPollQuestion("");
        setPollOptions(["", ""]);
        setPollSettings({
          shareResultsLive: false,
          showResultsAtEnd: false,
          allowMultipleAnswers: false,
          anonymousVoting: false,
          showParticipantsWhoVoted: false,
          showTotalVotesCountOnly: false,
          autoCloseAfter: false,
          autoCloseSeconds: 60,
        });

        // Navigate back or close modal after a short delay
        setTimeout(() => {
          navigate("/dashboard/polls"); // Go back to previous page
        }, 1000);
      } else {
        toast.error(response.message || "Failed to create poll.");
      }
    } catch (error) {
      logger("error", "Error creating poll:", error);
      const errorMessage =
        error.message || "An error occurred while creating the poll.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setPollQuestion("");
    setPollOptions(["", ""]);
    setPollSettings({
      shareResultsLive: false,
      showResultsAtEnd: false,
      allowMultipleAnswers: false,
      anonymousVoting: false,
      showParticipantsWhoVoted: false,
      showTotalVotesCountOnly: false,
      autoCloseAfter: false,
      autoCloseSeconds: 60,
    });
    // Navigate back
    navigate(-1);
  };

  return (
    <div className="relative mx-auto primary-card-background rounded-xl p-6 w-full max-w-2xl text-white max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold uppercase text-center w-full">
          CREATE NEW POLL
        </h3>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-white transition-colors ml-4"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Question Section */}
      <div className="mb-6">
        <label className="block text-white text-sm font-medium mb-2">
          Question
        </label>
        <input
          type="text"
          placeholder="Type your question here..."
          value={pollQuestion}
          onChange={(e) => setPollQuestion(e.target.value)}
          className="w-full px-4 py-3 bg-white text-gray-900 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#7700ff] focus:border-transparent"
        />
      </div>

      {/* Answers Section */}
      <div className="mb-6">
        <label className="block text-white text-sm font-medium mb-2">
          Answers
        </label>
        <div className="space-y-3">
          {pollOptions.map((option, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                placeholder={`Option ${index + 1}`}
                value={option}
                onChange={(e) => handleUpdatePollOption(index, e.target.value)}
                className="flex-1 px-4 py-3 bg-white text-gray-900 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#7700ff] focus:border-transparent"
              />
              {pollOptions.length > 2 && (
                <button
                  onClick={() => handleRemovePollOption(index)}
                  className="px-3 py-3 text-red-400 hover:text-red-300 transition-colors"
                  title="Remove option"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
          <button
            onClick={handleAddPollOption}
            className="ml-auto text-white hover:text-[#7700ff] transition-colors text-sm font-medium flex items-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Option
          </button>
        </div>
      </div>

      {/* Poll Settings */}
      <div className="mb-6 space-y-3">
        {/* Share results live */}
        <div className="flex items-center justify-between">
          <label className="text-white text-sm">Share results live</label>
          <button
            onClick={() => handleTogglePollSetting("shareResultsLive")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pollSettings.shareResultsLive ? "bg-[#7700ff]" : "bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                pollSettings.shareResultsLive
                  ? "translate-x-6"
                  : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Show results to participants at the end */}
        <div className="flex items-center justify-between">
          <label className="text-white text-sm">
            Show results to participants at the end
          </label>
          <button
            onClick={() => handleTogglePollSetting("showResultsAtEnd")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pollSettings.showResultsAtEnd ? "bg-[#7700ff]" : "bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                pollSettings.showResultsAtEnd
                  ? "translate-x-6"
                  : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Allow multiple answers */}
        <div className="flex items-center justify-between">
          <label className="text-white text-sm">Allow multiple answers</label>
          <button
            onClick={() => handleTogglePollSetting("allowMultipleAnswers")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pollSettings.allowMultipleAnswers ? "bg-[#7700ff]" : "bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                pollSettings.allowMultipleAnswers
                  ? "translate-x-6"
                  : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Anonymous voting */}
        <div className="flex items-center justify-between">
          <label className="text-white text-sm">Anonymous voting</label>
          <button
            onClick={() => handleTogglePollSetting("anonymousVoting")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pollSettings.anonymousVoting ? "bg-[#7700ff]" : "bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                pollSettings.anonymousVoting ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Show participants who voted */}
        <div className="flex items-center justify-between">
          <label className="text-white text-sm">
            Show participants who voted
          </label>
          <button
            onClick={() => handleTogglePollSetting("showParticipantsWhoVoted")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pollSettings.showParticipantsWhoVoted
                ? "bg-[#7700ff]"
                : "bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                pollSettings.showParticipantsWhoVoted
                  ? "translate-x-6"
                  : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Show total votes count only */}
        <div className="flex items-center justify-between">
          <label className="text-white text-sm">
            Show total votes count only
          </label>
          <button
            onClick={() => handleTogglePollSetting("showTotalVotesCountOnly")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pollSettings.showTotalVotesCountOnly
                ? "bg-[#7700ff]"
                : "bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                pollSettings.showTotalVotesCountOnly
                  ? "translate-x-6"
                  : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Auto-close after X seconds */}
        <div className="flex items-center justify-between">
          <label className="text-white text-sm">
            Auto-close after{" "}
            {pollSettings.autoCloseAfter && (
              <input
                type="number"
                min="1"
                value={pollSettings.autoCloseSeconds}
                onChange={(e) => handleUpdateAutoCloseSeconds(e.target.value)}
                className="w-16 px-2 py-1 ml-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#7700ff]"
                onClick={(e) => e.stopPropagation()}
              />
            )}{" "}
            seconds
          </label>
          <button
            onClick={() => handleTogglePollSetting("autoCloseAfter")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pollSettings.autoCloseAfter ? "bg-[#7700ff]" : "bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                pollSettings.autoCloseAfter ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end pt-4 border-t border-gray-700">
        <button
          className="px-6 py-3 rounded-lg bg-transparent border border-white text-white hover:bg-white/10 transition-colors"
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          className="px-6 py-3 rounded-lg bg-[#7700ff] text-white hover:bg-[#7700ff]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSubmitPoll}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create Poll"}
        </button>
      </div>
    </div>
  );
};

export default CreatePolls;
